import {
  normalizeNikolaActivity,
  type CreateNikolaRunRequest,
  type NikolaActivity,
  type NikolaBrowserActionLeaseResponse,
  type NikolaRunEventView,
  type NikolaRunView,
  type SubmitNikolaBrowserResultRequest,
} from "@chessla/shared";
import { apiClient, apiErrorFromResponse, apiFetch, ApiRequestError } from "@/shared/api/client";
import type { PendingAction, SendMessageResult } from "./chat-contracts";

const NIKOLA_REQUEST_TIMEOUT_MS = 330_000;
const untilAborted = async <T>(operation: Promise<T>, signal: AbortSignal): Promise<T> => {
  let abort!: () => void;
  const cancelled = new Promise<never>((_resolve, reject) => {
    abort = () =>
      reject(
        signal.reason instanceof Error
          ? signal.reason
          : new DOMException("Nikola request was cancelled.", "AbortError"),
      );
    if (signal.aborted) abort();
    else signal.addEventListener("abort", abort, { once: true });
  });
  try {
    return await Promise.race([operation, cancelled]);
  } finally {
    signal.removeEventListener("abort", abort);
  }
};

const nikolaRequest = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const controller = new AbortController();
  const abort = () => controller.abort(init?.signal?.reason);
  if (init?.signal?.aborted) abort();
  else init?.signal?.addEventListener("abort", abort, { once: true });
  const timeout = setTimeout(
    () =>
      controller.abort(
        new ApiRequestError(
          "Nikola's request timed out. Reopen this conversation to check its result before sending again.",
          "NIKOLA_REQUEST_TIMEOUT",
          408,
        ),
      ),
    NIKOLA_REQUEST_TIMEOUT_MS,
  );
  try {
    return await untilAborted(apiClient<T>(path, { ...init, signal: controller.signal }), controller.signal);
  } finally {
    clearTimeout(timeout);
    init?.signal?.removeEventListener("abort", abort);
  }
};

export interface NikolaRunInput extends Omit<CreateNikolaRunRequest, "requestId" | "context"> {
  requestId?: string;
  context?: CreateNikolaRunRequest["context"];
}

export interface NikolaBrowserResultInput extends SubmitNikolaBrowserResultRequest {
  runId: string;
}

export interface NikolaBrowserLeasePorts {
  claim(
    runId: string,
    actionId: string,
    leaseToken: string,
    signal?: AbortSignal,
  ): Promise<NikolaBrowserActionLeaseResponse>;
  heartbeat(
    runId: string,
    actionId: string,
    leaseToken: string,
    signal?: AbortSignal,
  ): Promise<NikolaBrowserActionLeaseResponse>;
}

export interface NikolaBrowserLeaseHandle {
  leaseToken: string;
  signal: AbortSignal;
  stop(): void;
}

const runEventTypes = new Set<NikolaRunEventView["type"]>([
  "planning",
  "reading",
  "calculating",
  "answering",
  "waiting",
  "finished",
]);
const FINAL_EVENT_DRAIN_MS = 1_000;

export const parseNikolaRunEventBlock = (block: string): NikolaRunEventView | null => {
  const data = block
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trimStart())
    .join("\n");
  if (!data) return null;
  try {
    const value = JSON.parse(data) as Partial<NikolaRunEventView>;
    if (!Number.isInteger(value.sequence) || typeof value.label !== "string" || !runEventTypes.has(value.type!))
      return null;
    const activity = value.activity === undefined ? undefined : normalizeNikolaActivity(value.activity);
    if (value.activity !== undefined && !activity) return null;
    if (
      activity &&
      (!activity.activityId || activity.sequence === undefined || activity.detail === undefined || !activity.occurredAt)
    )
      return null;
    return {
      sequence: value.sequence!,
      type: value.type!,
      label: value.label,
      ...(activity ? { activity: activity as NonNullable<NikolaRunEventView["activity"]> } : {}),
    };
  } catch {
    return null;
  }
};

const streamNikolaRunEvents = async (
  runId: string,
  after: number,
  onEvent: (event: NikolaRunEventView) => void,
  signal: AbortSignal,
): Promise<number> => {
  const response = await untilAborted(
    apiFetch(`/coach/runs/${encodeURIComponent(runId)}/events?after=${after}`, {
      headers: { Accept: "text/event-stream" },
      signal,
    }),
    signal,
  );
  if (!response.ok) throw await apiErrorFromResponse(response);
  if (!response.body) throw new Error("Nikola event stream is unavailable");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sequence = after;
  try {
    while (true) {
      const { done, value } = await untilAborted(reader.read(), signal);
      buffer += decoder.decode(value, { stream: !done }).replaceAll("\r\n", "\n");
      const blocks = buffer.split("\n\n");
      buffer = blocks.pop() ?? "";
      for (const block of blocks) {
        const event = parseNikolaRunEventBlock(block);
        if (!event || event.sequence <= sequence) continue;
        sequence = event.sequence;
        onEvent(event);
      }
      if (done) break;
    }
  } finally {
    void reader.cancel().catch(() => undefined);
  }
  return sequence;
};

const abortableDelay = (milliseconds: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

export const nikolaRunProgressActivity = (run: NikolaRunView): NikolaActivity | null => {
  if (run.progress.total <= 0 || !run.progress.label.startsWith("Analyzing games")) return null;
  return {
    kind: "analysis_started",
    status: run.status === "completed" ? "complete" : "running",
    label: run.progress.label.toLocaleUpperCase(),
    progress: Math.round((run.progress.completed / run.progress.total) * 100),
  };
};

const reportRunProgress = (run: NikolaRunView, onActivity: (activity: NikolaActivity) => void): void => {
  const activity = nikolaRunProgressActivity(run);
  if (activity) onActivity(activity);
};

export const nikolaActivityFromRunEvent = (event: NikolaRunEventView): NikolaActivity | null => {
  if (event.activity) return event.activity;
  if (event.type === "reading") return null;
  const comparing = event.type === "calculating" || event.type === "waiting";
  return {
    kind: comparing ? "line_compared" : "coach_thinking",
    status: event.type === "finished" ? "complete" : "running",
    label: comparing
      ? "COMPARING VERIFIED CHESS EVIDENCE"
      : event.type === "finished"
        ? "ANSWER BUILT"
        : "BUILDING YOUR ANSWER",
  };
};

const withNikolaRunEvents = async <T>(
  runId: string,
  onActivity: (activity: NikolaActivity) => void,
  signal: AbortSignal | undefined,
  operation: () => Promise<T>,
): Promise<T> => {
  let settled = false;
  let sequence = -1;
  const eventController = new AbortController();
  const abortEvents = () => eventController.abort();
  signal?.addEventListener("abort", abortEvents, { once: true });
  const receive = (event: NikolaRunEventView): void => {
    sequence = Math.max(sequence, event.sequence);
    const activity = nikolaActivityFromRunEvent(event);
    if (activity) onActivity(activity);
  };
  const watch = (async () => {
    while (!settled && !eventController.signal.aborted) {
      try {
        sequence = await streamNikolaRunEvents(runId, sequence, receive, eventController.signal);
      } catch {
        if (eventController.signal.aborted) return;
        await abortableDelay(500, eventController.signal).catch(() => undefined);
      }
    }
  })();
  try {
    const result = await operation();
    settled = true;
    eventController.abort();
    await watch.catch(() => undefined);
    if (!signal?.aborted) {
      const drainController = new AbortController();
      const abortDrain = () => drainController.abort();
      const timeout = globalThis.setTimeout(abortDrain, FINAL_EVENT_DRAIN_MS);
      signal?.addEventListener("abort", abortDrain, { once: true });
      try {
        sequence = await streamNikolaRunEvents(runId, sequence, receive, drainController.signal);
      } catch {
        // The answer is authoritative; a bounded activity replay must not replace it with an error.
      } finally {
        globalThis.clearTimeout(timeout);
        signal?.removeEventListener("abort", abortDrain);
      }
    }
    return result;
  } finally {
    settled = true;
    eventController.abort();
    signal?.removeEventListener("abort", abortEvents);
    await watch.catch(() => undefined);
  }
};

export const claimNikolaBrowserAction = (
  runId: string,
  actionId: string,
  leaseToken: string,
  signal?: AbortSignal,
): Promise<NikolaBrowserActionLeaseResponse> =>
  nikolaRequest<NikolaBrowserActionLeaseResponse>(`/coach/runs/${encodeURIComponent(runId)}/browser-actions/claim`, {
    method: "POST",
    body: JSON.stringify({ actionId, leaseToken }),
    signal,
  });

export const heartbeatNikolaBrowserAction = (
  runId: string,
  actionId: string,
  leaseToken: string,
  signal?: AbortSignal,
): Promise<NikolaBrowserActionLeaseResponse> =>
  nikolaRequest<NikolaBrowserActionLeaseResponse>(
    `/coach/runs/${encodeURIComponent(runId)}/browser-actions/heartbeat`,
    {
      method: "POST",
      body: JSON.stringify({ actionId, leaseToken }),
      signal,
    },
  );

const waitForLease = (milliseconds: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason instanceof Error ? signal.reason : new Error("Nikola browser action was cancelled."));
      return;
    }
    const abort = () => {
      clearTimeout(timeout);
      reject(signal?.reason instanceof Error ? signal.reason : new Error("Nikola browser action was cancelled."));
    };
    const timeout = setTimeout(() => {
      signal?.removeEventListener("abort", abort);
      resolve();
    }, milliseconds);
    signal?.addEventListener("abort", abort, { once: true });
  });

const browserLeasePorts: NikolaBrowserLeasePorts = {
  claim: claimNikolaBrowserAction,
  heartbeat: heartbeatNikolaBrowserAction,
};

export const acquireNikolaBrowserActionLease = async (
  action: PendingAction,
  parentSignal?: AbortSignal,
  ports: NikolaBrowserLeasePorts = browserLeasePorts,
): Promise<NikolaBrowserLeaseHandle | null> => {
  if (!action.runId || !action.actionId) throw new Error("Nikola browser action is missing run identity");
  const leaseToken = crypto.randomUUID();
  const startedAt = Date.now();
  let response = await ports.claim(action.runId, action.actionId, leaseToken, parentSignal);
  while (response.status === "busy") {
    if (Date.now() - startedAt >= NIKOLA_REQUEST_TIMEOUT_MS)
      throw new Error("Nikola's browser action is still running elsewhere. Reopen this conversation to check it.");
    await waitForLease(Math.min(1_000, Math.max(250, response.retryAfterMs)), parentSignal);
    response = await ports.claim(action.runId, action.actionId, leaseToken, parentSignal);
  }
  if (response.status === "settled") return null;

  const controller = new AbortController();
  const abort = () => controller.abort(parentSignal?.reason);
  parentSignal?.addEventListener("abort", abort, { once: true });
  let stopped = false;
  let refreshing = false;
  let expiry: ReturnType<typeof setTimeout> | undefined;
  const renewExpiry = (expiresAt: string): void => {
    clearTimeout(expiry);
    const remaining = Date.parse(expiresAt) - Date.now();
    if (!Number.isFinite(remaining) || remaining <= 0) {
      controller.abort(new Error("Nikola browser action lease expired."));
      return;
    }
    expiry = setTimeout(() => controller.abort(new Error("Nikola browser action lease expired.")), remaining);
  };
  const heartbeat = setInterval(() => {
    if (refreshing || stopped) return;
    refreshing = true;
    void ports
      .heartbeat(action.runId!, action.actionId!, leaseToken, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        if (result.status === "claimed") renewExpiry(result.expiresAt);
        else controller.abort(new Error("Nikola browser action lease was lost."));
      })
      .catch(() => controller.abort(new Error("Nikola browser action lease could not be renewed.")))
      .finally(() => {
        refreshing = false;
      });
  }, 5_000);
  controller.signal.addEventListener(
    "abort",
    () => {
      stopped = true;
      clearInterval(heartbeat);
      clearTimeout(expiry);
      parentSignal?.removeEventListener("abort", abort);
    },
    { once: true },
  );
  if (parentSignal?.aborted) abort();
  else renewExpiry(response.expiresAt);
  controller.signal.throwIfAborted();
  return {
    leaseToken,
    signal: controller.signal,
    stop: () => {
      if (stopped) return;
      stopped = true;
      clearInterval(heartbeat);
      clearTimeout(expiry);
      controller.abort();
      parentSignal?.removeEventListener("abort", abort);
    },
  };
};

export const nikolaBrowserResultInput = (
  action: PendingAction,
  result: Pick<NikolaBrowserResultInput, "leaseToken" | "artifactId" | "gameId" | "failed">,
): NikolaBrowserResultInput => {
  if (!action.runId || !action.actionId || !action.stepId || !action.idempotencyKey) {
    throw new Error("Nikola browser action is missing run identity");
  }
  return {
    runId: action.runId,
    actionId: action.actionId,
    stepId: action.stepId,
    idempotencyKey: action.idempotencyKey,
    ...result,
  };
};

const pendingAction = (run: NikolaRunView): PendingAction | null => {
  if (run.status !== "waiting_client") return null;
  const action = run.pendingAction;
  if (!action) return null;
  const identity = {
    runId: run.id,
    actionId: action.id,
    stepId: action.stepId,
    idempotencyKey: action.idempotencyKey,
  };
  return action.kind === "analyze_game"
    ? {
        type: "start_game_analysis",
        gameId: action.gameId,
        analysisPreset: action.analysisPreset,
        analysisMode: action.analysisMode,
        ...identity,
      }
    : { type: "browser_engine", request: action.request, ...identity };
};

export const nikolaRunResult = (run: NikolaRunView): SendMessageResult => {
  if (run.status === "completed" && run.answer?.text.trim()) {
    return {
      reply: run.answer.text,
      parts: run.answer.parts,
      status: "complete",
      pendingAction: null,
      creditsCharged: run.answer.creditsCharged,
    };
  }
  const action = pendingAction(run);
  if (action) {
    return {
      reply: "",
      status: "thinking",
      pendingAction: action,
      creditsCharged: 0,
    };
  }
  return {
    reply: "",
    status: "failed",
    pendingAction: null,
    creditsCharged: 0,
    failureCode: run.failure?.code ?? "request_failed",
    retryable: run.failure?.retryable ?? false,
    ...(run.failure?.message ? { failureMessage: run.failure.message } : {}),
  };
};

export const createNikolaRun = async (
  input: NikolaRunInput,
  onActivity: (activity: NikolaActivity) => void = () => undefined,
  signal?: AbortSignal,
  onCreated: (runId: string) => void = () => undefined,
): Promise<SendMessageResult> => {
  const request = { ...input, requestId: input.requestId ?? crypto.randomUUID() };
  const created = await nikolaRequest<NikolaRunView>("/coach/runs", {
    method: "POST",
    body: JSON.stringify(request),
    signal,
  });
  onCreated(created.id);
  reportRunProgress(created, onActivity);
  const terminal =
    created.status !== "planning" && created.status !== "running" && created.status !== "grading"
      ? created
      : await withNikolaRunEvents(created.id, onActivity, signal, () =>
          nikolaRequest<NikolaRunView>(`/coach/runs/${encodeURIComponent(created.id)}/resume`, {
            method: "POST",
            signal,
          }),
        );
  reportRunProgress(terminal, onActivity);
  return nikolaRunResult(terminal);
};

export const recoverNikolaRun = async (
  sessionId: string,
  onActivity: (activity: NikolaActivity) => void = () => undefined,
  signal?: AbortSignal,
  onFound: (runId: string) => void = () => undefined,
): Promise<SendMessageResult | null> => {
  const run = await nikolaRequest<NikolaRunView | null>(`/coach/sessions/${encodeURIComponent(sessionId)}/active-run`, {
    signal,
  });
  if (!run) return null;
  onFound(run.id);
  reportRunProgress(run, onActivity);
  if (run.status === "waiting_client") return nikolaRunResult(run);
  const resumed = await withNikolaRunEvents(run.id, onActivity, signal, () =>
    nikolaRequest<NikolaRunView>(`/coach/runs/${encodeURIComponent(run.id)}/resume`, { method: "POST", signal }),
  );
  reportRunProgress(resumed, onActivity);
  return nikolaRunResult(resumed);
};

export const submitNikolaBrowserResult = async (
  input: NikolaBrowserResultInput,
  onActivity: (activity: NikolaActivity) => void = () => undefined,
  signal?: AbortSignal,
): Promise<SendMessageResult> => {
  onActivity({ kind: "coach_thinking", status: "running", label: "VERIFYING THE RESULT" });
  const { runId, ...body } = input;
  const run = await withNikolaRunEvents(runId, onActivity, signal, () =>
    nikolaRequest<NikolaRunView>(`/coach/runs/${encodeURIComponent(runId)}/browser-results`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    }),
  );
  reportRunProgress(run, onActivity);
  return nikolaRunResult(run);
};

export const cancelNikolaRun = (runId: string): Promise<NikolaRunView> =>
  nikolaRequest<NikolaRunView>(`/coach/runs/${encodeURIComponent(runId)}/cancel`, { method: "POST" });
